import { useEffect, useRef, useState } from "react";

/**
 * Branded launch splash shown once when the app opens/loads.
 * Reveals the Coach JP logo, holds briefly, then fades out and unmounts.
 * Handles the cache-hit case where the preloaded image is already `complete`
 * before React attaches onLoad (in which case onLoad never fires).
 * Only mounts on a fresh page load (App mounts once), not on SPA route changes.
 */
export default function BootSplash() {
  const [loaded, setLoaded] = useState(false);
  const [phase, setPhase] = useState("in"); // in -> out -> gone
  const imgRef = useRef(null);
  const startedRef = useRef(false);

  const reveal = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setLoaded(true);
    // Hold the fully-visible logo briefly, then fade out.
    setTimeout(() => setPhase((p) => (p === "gone" ? p : "out")), 1300);
  };

  useEffect(() => {
    // Cache hit: image may already be decoded before onLoad can attach.
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) reveal();
    // Safety fallback: fade out even if the image is slow or fails.
    const maxT = setTimeout(() => setPhase((p) => (p === "gone" ? p : "out")), 3200);
    return () => clearTimeout(maxT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== "out") return;
    const t = setTimeout(() => setPhase("gone"), 550);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === "gone") return null;

  return (
    <div
      data-testid="boot-splash"
      className="fixed inset-0 z-[9999] bg-[#070707] flex flex-col items-center justify-center"
      style={{
        opacity: phase === "out" ? 0 : 1,
        transition: "opacity 500ms ease",
        pointerEvents: phase === "out" ? "none" : "auto",
      }}
    >
      <img
        ref={imgRef}
        src="/coach-jp-splash.png"
        alt="Coach JP"
        onLoad={reveal}
        onError={reveal}
        className="w-auto max-h-[85vh] max-w-[92vw] object-contain select-none"
        draggable={false}
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? "scale(1)" : "scale(0.94)",
          transition: "opacity 700ms ease, transform 900ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      />
      <div className="absolute bottom-16 flex items-center gap-1.5" aria-hidden="true">
        <span className="w-1.5 h-1.5 rounded-full bg-[#d7ff00] boot-dot" style={{ animationDelay: "0ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-[#d7ff00] boot-dot" style={{ animationDelay: "150ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-[#d7ff00] boot-dot" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}
